import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const TestimonialsCarousel = () => {
  const [currentStory, setCurrentStory] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const companyStories = [
    {
      id: 1,
      title: "Our Vision",
      subtitle: "Connecting Africa to the World",
      content: `At Exquisite Coral Africa, we envision a future where premium African agricultural products reach every corner of the globe. We're committed to bridging the gap between Africa's rich agricultural heritage and international markets, ensuring fair trade and sustainable practices that benefit both producers and consumers.`,
      icon: "Eye",
      highlights: [
        "Global market access for African agriculture",
        "Sustainable and ethical trading practices",
        "Premium quality standards",
        "Fair compensation for local farmers"
      ]
    },
    {
      id: 2,
      title: "Our Mission",
      subtitle: "Excellence in Agricultural Exports",
      content: `We specialize in the export of premium agricultural products including corn, moringa leaves and oil, shea butter, and shea nuts. Our mission is to deliver exceptional value-added products that meet international quality standards while supporting sustainable farming communities across Africa.`,
      icon: "Target",
      highlights: [
        "Specialized in 5 premium product categories",
        "Value-added processing capabilities",
        "International quality certifications",
        "Direct support to farming communities"
      ]
    },
    {
      id: 3,
      title: "Our Commitment",
      subtitle: "Quality You Can Trust",
      content: `As a new company, we understand the importance of earning trust through consistent quality and reliable service. We're committed to implementing the highest standards in product sourcing, processing, and delivery. Every product that bears our name represents our dedication to excellence.`,
      icon: "Shield",
      highlights: [
        "Rigorous quality control processes",
        "Full traceability from farm to export",
        "International compliance standards",
        "Continuous improvement mindset"
      ]
    },
    {
      id: 4,
      title: "Our Promise",
      subtitle: "Your Success is Our Success",
      content: `We promise to be more than just a supplier - we're your strategic partner in accessing premium African agricultural products. From initial inquiry to final delivery, we're committed to providing exceptional service and building long-term partnerships based on trust and mutual success.`,
      icon: "Handshake",
      highlights: [
        "Dedicated customer support team",
        "Flexible order quantities",
        "Competitive pricing structure",
        "Long-term partnership approach"
      ]
    }
  ];

  useEffect(() => {
    if (isAutoPlaying) {
      const interval = setInterval(() => {
        setCurrentStory((prev) => (prev + 1) % companyStories.length);
      }, 6000);

      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, companyStories.length]);

  const nextStory = () => {
    setCurrentStory((prev) => (prev + 1) % companyStories.length);
  };

  const prevStory = () => {
    setCurrentStory((prev) => (prev - 1 + companyStories.length) % companyStories.length);
  };

  const currentContent = companyStories[currentStory];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Our Story & Commitment
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Learn about our vision, mission, and unwavering commitment to delivering premium African agricultural products to international markets.
          </p>
        </motion.div>

        {/* Story Carousel */}
        <div className="relative">
          <div className="glass-card p-8 lg:p-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentContent.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="flex flex-col items-center text-center"
              >
                {/* Content */}
                <div className="space-y-8 max-w-3xl mx-auto">
                  {/* Icon and Title Section */}
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
                      <Icon name={currentContent.icon} size={36} color="white" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-4xl font-bold text-primary leading-tight">{currentContent.title}</h3>
                      <p className="text-xl text-accent font-semibold">{currentContent.subtitle}</p>
                    </div>
                  </div>

                  {/* Main Content */}
                  <p className="text-text-secondary text-lg leading-relaxed max-w-2xl mx-auto">
                    {currentContent.content}
                  </p>

                  {/* Key Highlights */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-bold text-primary">Key Highlights</h4>
                    <div className="grid gap-4 text-left max-w-xl mx-auto">
                      {currentContent.highlights.map((highlight, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Icon name="Check" size={14} color="white" />
                          </div>
                          <span className="text-text-secondary leading-relaxed">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={prevStory}
              className="flex items-center space-x-2 px-4 py-2 text-primary hover:text-accent transition-colors duration-250"
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              <Icon name="ChevronLeft" size={20} />
              <span>Previous</span>
            </button>

            {/* Story Indicators */}
            <div className="flex space-x-2">
              {companyStories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStory(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentStory
                      ? 'bg-primary scale-125'
                      : 'bg-surface-300 hover:bg-surface-400'
                    }`}
                  onMouseEnter={() => setIsAutoPlaying(false)}
                  onMouseLeave={() => setIsAutoPlaying(true)}
                />
              ))}
            </div>

            <button
              onClick={nextStory}
              className="flex items-center space-x-2 px-4 py-2 text-primary hover:text-accent transition-colors duration-250"
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              <span>Next</span>
              <Icon name="ChevronRight" size={20} />
            </button>
          </div>
        </div>

        {/* Company Principles */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="mt-20 glass-card p-8"
        >
          <h3 className="text-2xl font-bold text-primary text-center mb-8">
            Our Core Principles
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-accent mb-2">Quality</div>
              <div className="text-text-secondary">Never compromise on product standards</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-success mb-2">Trust</div>
              <div className="text-text-secondary">Build lasting partnerships</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">Innovation</div>
              <div className="text-text-secondary">Continuous improvement in processes</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-accent mb-2">Sustainability</div>
              <div className="text-text-secondary">Ethical and eco-friendly practices</div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          className="mt-12 text-center"
        >
          <h3 className="text-2xl font-bold text-primary mb-4">
            Ready to Work Together?
          </h3>
          <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
            We're excited to start our journey in the export business and would love to partner with you. Contact us to discuss your agricultural product needs.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('export-inquiry');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="btn-accent px-8 py-4 font-semibold flex items-center space-x-2 mx-auto"
          >
            <Icon name="MessageCircle" size={20} />
            <span>Start the Conversation</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;